import { create } from 'zustand';
import type { GameKbArtifacts, GameKbMeta, GameKbStage } from '@sr-test-wizard/shared';
import { gameKbApi } from '../lib/gameKbApi';

/**
 * Live progress snapshot for one game-kb stage (description / report / chat),
 * fed from the `game-kb-progress` WS stream. `tool` is the last file
 * operation label so the page can show a "tracing the package" line.
 */
export interface GameKbStageProgress {
  status: 'started' | 'progress' | 'completed' | 'failed';
  message?: string;
  tool?: string;
}

function emptyMeta(): GameKbMeta {
  const blank = { status: 'idle' as const, model: null, updatedAt: null, error: null };
  return { description: { ...blank }, report: { ...blank } };
}

interface GameKbState {
  runId: string | null;
  description: string | null;
  report: string | null;
  meta: GameKbMeta;
  loading: boolean;
  error: string | null;
  /** Per-stage live progress, keyed by stage. */
  progress: Partial<Record<GameKbStage, GameKbStageProgress>>;

  load: (runId: string) => Promise<void>;
  generateDescription: () => Promise<void>;
  generateReport: () => Promise<void>;
  applyProgress: (stage: GameKbStage, snapshot: GameKbStageProgress) => void;
  refetchArtifacts: () => Promise<void>;
  clear: () => void;
}

export const useGameKbStore = create<GameKbState>((set, get) => ({
  runId: null,
  description: null,
  report: null,
  meta: emptyMeta(),
  loading: false,
  error: null,
  progress: {},

  async load(runId) {
    const isDifferent = get().runId !== null && get().runId !== runId;
    set({
      runId,
      loading: true,
      error: null,
      ...(isDifferent ? { description: null, report: null, meta: emptyMeta(), progress: {} } : {}),
    });
    try {
      const data: GameKbArtifacts = await gameKbApi.get(runId);
      set({ description: data.description, report: data.report, meta: data.meta });
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  async generateDescription() {
    const { runId } = get();
    if (!runId) return;
    set((s) => ({
      meta: { ...s.meta, description: { ...s.meta.description, status: 'running', error: null } },
      progress: { ...s.progress, description: { status: 'started' } },
      error: null,
    }));
    try {
      await gameKbApi.generateDescription(runId);
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  async generateReport() {
    const { runId } = get();
    if (!runId) return;
    set((s) => ({
      meta: { ...s.meta, report: { ...s.meta.report, status: 'running', error: null } },
      progress: { ...s.progress, report: { status: 'started' } },
      error: null,
    }));
    try {
      await gameKbApi.generateReport(runId);
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  applyProgress(stage, snapshot) {
    set((s) => ({ progress: { ...s.progress, [stage]: snapshot } }));
    // On terminal status for a report stage, pull the freshly written file.
    if (
      (stage === 'description' || stage === 'report') &&
      (snapshot.status === 'completed' || snapshot.status === 'failed')
    ) {
      void get().refetchArtifacts();
    }
  },

  async refetchArtifacts() {
    const { runId } = get();
    if (!runId) return;
    try {
      const data = await gameKbApi.get(runId);
      set({ description: data.description, report: data.report, meta: data.meta });
    } catch {
      // keep existing state; WS already logged the error
    }
  },

  clear() {
    set({
      runId: null,
      description: null,
      report: null,
      meta: emptyMeta(),
      loading: false,
      error: null,
      progress: {},
    });
  },
}));
